import {
  EventFilter,
  EventQuery,
  PersistedRuntimeEvent,
  RetentionPolicy,
  RuntimeEvent,
  RuntimeEventSensitivity
} from '../contracts'
import { RuntimeEventRouter } from '../routing/router'
import { DEFAULT_RETENTION_POLICY } from '../retention/policy'
import { RuntimeReplayService } from '../replay/service'

export class RuntimeEventStore {
  private events: PersistedRuntimeEvent[] = []
  private sequence = 0
  private readonly policy: RetentionPolicy

  private indexByExecutionId = new Map<string, Set<number>>()
  private indexByCorrelationId = new Map<string, Set<number>>()
  private indexByStream = new Map<string, Set<number>>()
  private indexByCategory = new Map<string, Set<number>>()

  constructor(policy: RetentionPolicy = DEFAULT_RETENTION_POLICY) {
    this.policy = policy
  }

  persist(event: RuntimeEvent): PersistedRuntimeEvent {
    this.evictExpired()

    const routing = RuntimeEventRouter.route(event)
    const now = new Date()
    const ttlMs = this.resolveTtlMs(event.category, routing.sensitivity)
    const expiresAt = event.metadata?.expiresAt || (ttlMs > 0 ? new Date(now.getTime() + ttlMs) : null)

    const persisted: PersistedRuntimeEvent = {
      ...event,
      sequence: ++this.sequence,
      stream: routing.stream,
      severity: routing.severity,
      sensitivity: routing.sensitivity,
      expiresAt,
      persistedAt: now
    }

    this.events.push(persisted)
    this.addIndex(this.indexByExecutionId, persisted.context.executionId, persisted.sequence)
    this.addIndex(this.indexByCorrelationId, persisted.context.correlationId, persisted.sequence)
    this.addIndex(this.indexByStream, persisted.stream, persisted.sequence)
    this.addIndex(this.indexByCategory, persisted.category, persisted.sequence)

    this.enforceMaxEvents()
    return persisted
  }

  query(query: EventQuery): PersistedRuntimeEvent[] {
    this.evictExpired()
    return RuntimeReplayService.query(this.events, query)
  }

  replay(filter: EventFilter = {}): PersistedRuntimeEvent[] {
    this.evictExpired()
    return RuntimeReplayService.replayWindow(this.events, filter)
  }

  replayByExecution(executionId: string): PersistedRuntimeEvent[] {
    this.evictExpired()
    return RuntimeReplayService.replayByExecution(this.events, executionId)
  }

  replayByCorrelation(correlationId: string): PersistedRuntimeEvent[] {
    this.evictExpired()
    return RuntimeReplayService.replayByCorrelation(this.events, correlationId)
  }

  all(): PersistedRuntimeEvent[] {
    this.evictExpired()
    return [...this.events]
  }

  count(): number {
    this.evictExpired()
    return this.events.length
  }

  private resolveTtlMs(category: string, sensitivity: RuntimeEventSensitivity): number {
    return (
      this.policy.sensitivityTtlMs[sensitivity] ||
      this.policy.categoryTtlMs[category as any] ||
      this.policy.defaultTtlMs
    )
  }

  private evictExpired(): void {
    const now = Date.now()
    const active = this.events.filter((event) => !event.expiresAt || event.expiresAt.getTime() > now)
    if (active.length === this.events.length) return

    this.events = active
    this.rebuildIndexes()
  }

  private enforceMaxEvents(): void {
    if (this.events.length <= this.policy.maxEvents) return
    const overflow = this.events.length - this.policy.maxEvents
    this.events = this.events.slice(overflow)
    this.rebuildIndexes()
  }

  private rebuildIndexes(): void {
    this.indexByExecutionId.clear()
    this.indexByCorrelationId.clear()
    this.indexByStream.clear()
    this.indexByCategory.clear()

    for (const event of this.events) {
      this.addIndex(this.indexByExecutionId, event.context.executionId, event.sequence)
      this.addIndex(this.indexByCorrelationId, event.context.correlationId, event.sequence)
      this.addIndex(this.indexByStream, event.stream, event.sequence)
      this.addIndex(this.indexByCategory, event.category, event.sequence)
    }
  }

  private addIndex(index: Map<string, Set<number>>, key: string, sequence: number): void {
    if (!index.has(key)) {
      index.set(key, new Set<number>())
    }
    index.get(key)!.add(sequence)
  }
}
